import { base64ToBlob } from '@shared/util/base64ToBlob';
import { type FileMetaSource, getFileMetaText } from '@shared/util/fileMeta';

import docxIcon from '@assets/icons/docx.svg';
import gifIcon from '@assets/icons/gif.svg';
import jpgIcon from '@assets/icons/jpg.svg';
import mp3Icon from '@assets/icons/mp3.svg';
import mp4Icon from '@assets/icons/mp4.svg';
import pdfIcon from '@assets/icons/pdf.svg';
import pngIcon from '@assets/icons/png.svg';
import xlsxIcon from '@assets/icons/xlsx.svg';

import { FileContent, FilePreview } from './FilePreview';
import { FileResponse, ShadcnUploadFileVM } from './ShadcnUploadFlowClient';

import { css } from '@emotion/css';
import { ShadcnButton, ShadcnDrawerProps } from '@shadcn-features/core';
import { cx } from 'class-variance-authority';
import { Download, FileText, Trash } from 'lucide-react';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type FileCardDeletePopconfirm = {
    onConfirm: () => void;
    title?: string;
};

interface FileCardProps<T extends ShadcnUploadFileVM> {
    file: T;
    extra?: (file: T) => ReactNode;
    onDownload?: (
        params: T,
    ) => Promise<FileContent | undefined> | FileContent | undefined;
    onDownloadBlob?: (params: T) => Promise<void> | void;
    downloadLabel?: ReactNode;
    downloadBlobLabel?: ReactNode;
    hideDownloadButton?: boolean;
    onDelete?: (params: T) => Promise<void> | void;
    onPreview?: (params: T) => Promise<void> | void;
    disabled?: boolean;
    previewDrawerProps?: ShadcnDrawerProps;
    imageUrl?: string | null;
    thumbnail?: ReactNode;
    enablePdfAiAnalysis?: boolean;
    wrapperTypePreview?: React.ComponentPropsWithoutRef<
        typeof FilePreview
    >['wrapperType'];
}

export const FileCardFlow = <T extends ShadcnUploadFileVM>({
    imageUrl,
    thumbnail,
    onDelete,
    onDownload,
    onDownloadBlob,
    downloadLabel,
    downloadBlobLabel,
    hideDownloadButton,
    onPreview,
    file,
    previewDrawerProps,
    disabled,
    enablePdfAiAnalysis,
    wrapperTypePreview,
}: FileCardProps<T>) => {
    const { t } = useTranslation();
    const fileType = file.name.split('.')[1];
    const metaText = getFileMetaText(file as FileMetaSource);

    const previewIcon = useMemo(() => {
        let src: string | null = null;

        switch (fileType) {
            case 'xlsx':
                src = xlsxIcon;
                break;
            case 'jpg':
                src = jpgIcon;
                break;
            case 'png':
                src = pngIcon;
                break;
            case 'mp4':
                src = mp4Icon;
                break;
            case 'pdf':
                src = pdfIcon;
                break;
            case 'docx':
                src = docxIcon;
                break;
            case 'gif':
                src = gifIcon;
                break;
            case 'mp3':
                src = mp3Icon;
                break;
            default:
                break;
        }

        if (!src)
            return (
                <FileText
                    size={32}
                    className={css`
                        color: #ff4d4f;
                        flex-shrink: 0;
                    `}
                />
            );

        return (
            <img
                alt='previewIcon'
                src={src}
                className={css`
                    width: 32px;
                    height: 32px;
                    flex-shrink: 0;
                `}
            />
        );
    }, [fileType]);

    return (
        <div>
            <div
                className={css`
                    border: 1px solid var(--color-border);
                    border-radius: 8px;
                    padding: 16px;
                    background: var(--color-body);
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    min-height: 65px;
                `}
            >
                {thumbnail ??
                    (imageUrl ? (
                        <img
                            alt=''
                            src={imageUrl}
                            className={css`
                                width: 65px;
                                height: 65px;
                                object-fit: cover;
                                border-radius: 8px;
                                flex-shrink: 0;
                            `}
                        />
                    ) : (
                        previewIcon
                    ))}
                <div
                    className={css`
                        flex: 1;
                        min-width: 0;
                    `}
                >
                    <div className='flex min-w-0 flex-col gap-0.5'>
                        <div
                            className={cx(
                                css`
                                    font-size: 14px;
                                    font-weight: 500;
                                    color: var(--color-card-text);
                                `,
                                'font-body truncate',
                            )}
                        >
                            {file.name}
                        </div>
                        {metaText && (
                            <div className='truncate text-[11px] leading-4 text-(--color-card-subtitle)'>
                                {metaText}
                            </div>
                        )}
                    </div>
                </div>
                <div
                    className={css`
                        display: flex;
                        gap: 8px;
                        flex-shrink: 0;
                    `}
                >
                    {!disabled && onDelete && file && (
                        <ShadcnButton
                            className={cx('font-body flex items-center gap-2')}
                            icon={<Trash size={16} />}
                            size='sm'
                            type='button'
                            variant='destructiveLk'
                            popconfirm={{
                                onConfirm: () => {
                                    void onDelete(file);
                                },
                                title: t('translation:areYouSureDelete'),
                            }}
                        />
                    )}

                    <FilePreview
                        drawerProps={previewDrawerProps}
                        enablePdfAiAnalysis={enablePdfAiAnalysis}
                        file={file}
                        wrapperType={wrapperTypePreview}
                        previewText={
                            <span className='font-inter hidden text-(--color-simple) lg:block'>
                                {t('translation:preview')}
                            </span>
                        }
                        triggerProps={{
                            variant: 'outlineLk',
                        }}
                        onDownload={onDownload}
                        onPreview={onPreview}
                    />
                    {onDownloadBlob ? (
                        <ShadcnButton
                            size={'sm'}
                            type='button'
                            variant='outlineLk'
                            icon={
                                <Download
                                    className={css`
                                        height: 0.875rem;
                                        width: 0.875rem;
                                    `}
                                />
                            }
                            onClick={async () => {
                                await onDownloadBlob(file);
                            }}
                        >
                            <span className='font-body hidden text-(--color-simple) lg:block'>
                                {downloadBlobLabel ?? t('translation:download')}
                            </span>
                        </ShadcnButton>
                    ) : (
                        onDownload &&
                        !hideDownloadButton && (
                            <ShadcnButton
                                size={'sm'}
                                type='button'
                                variant='outlineLk'
                                icon={
                                    <Download
                                        className={css`
                                            height: 0.875rem;
                                            width: 0.875rem;
                                        `}
                                    />
                                }
                                onClick={async () => {
                                    const data = await onDownload(file);

                                    if (!data || !data.content) return;
                                    const fileResponse = data as FileResponse;
                                    const contentType =
                                        'contentType' in fileResponse
                                            ? fileResponse.contentType
                                            : 'extension' in fileResponse
                                              ? fileResponse.extension
                                              : data.extension;
                                    const fileName =
                                        'originalFileName' in fileResponse
                                            ? fileResponse.originalFileName
                                            : 'name' in fileResponse
                                              ? fileResponse.name
                                              : data.name;

                                    const blob = base64ToBlob(
                                        data.content,
                                        contentType,
                                    );
                                    const url = URL.createObjectURL(blob);
                                    const downloadLink =
                                        document.createElement('a');
                                    downloadLink.href = url;
                                    downloadLink.download =
                                        fileName || file.name;
                                    downloadLink.click();
                                    downloadLink.remove();
                                    URL.revokeObjectURL(url);
                                }}
                            >
                                <span className='font-body hidden text-(--color-simple) lg:block'>
                                    {downloadLabel ?? t('translation:download')}
                                </span>
                            </ShadcnButton>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};
